import * as THREE from 'three/webgpu';
import type { Viewer } from '../../viewer/Viewer';
import { ImportedModelRaycastCandidatePolicy } from './candidates';
import { ImportedModelSelectionObjectResolver } from './ImportedModelSelectionObjectResolver';
import {
    isImportedTopologySelectionObject,
    resolveImportedTopologyVisibleSourceMesh,
} from './ImportedModelTopologyPickResolver';

/**
 * 导入模型射线命中解析器依赖。
 */
export interface ImportedModelRaycastResolverOptions {
    scene: THREE.Scene;
    getSceneObjectById?: (objectId: string) => THREE.Object3D | null;
}

/**
 * 导入模型射线命中解析器。
 *
 * 设计说明：
 * 1. 该解析器只负责 imported-model 的候选收集与 selection proxy 归一化；
 * 2. 默认模式 handler 通过组合该解析器扩展 GLB 拾取，而不修改抽象基类；
 * 3. 这样可以把 imported-model 特殊规则与 sketch/feature 的通用规则解耦。
 */
export class ImportedModelRaycastResolver {
    private readonly options: ImportedModelRaycastResolverOptions;
    private readonly selectionObjectResolver: ImportedModelSelectionObjectResolver;
    /** 构造参数来自 Viewer 时保留其稳定实例化门面，避免选择层直接依赖低层 Registry。 */
    private readonly viewer: Viewer | null;
    /** 缓存投影和兼容无 Viewer 查询使用相同资格规则。 */
    private readonly candidatePolicy: ImportedModelRaycastCandidatePolicy;

    /**
     * 创建导入模型射线命中解析器。
     * @param options 运行时依赖。
     */
    public constructor(options: ImportedModelRaycastResolverOptions | Viewer) {
        const viewer = typeof Reflect.get(options, 'isImportedModelInstancedMesh') === 'function'
            ? options as Viewer
            : null;
        this.viewer = viewer;
        this.candidatePolicy = new ImportedModelRaycastCandidatePolicy(
            object => this.viewer?.isImportedModelInstancedMesh(object) ?? false,
        );
        this.options = viewer
            ? {
                scene: viewer.scene,
                getSceneObjectById: typeof viewer.getSceneObjectById === 'function'
                    ? viewer.getSceneObjectById.bind(viewer)
                    : undefined,
            }
            : options;
        this.selectionObjectResolver = new ImportedModelSelectionObjectResolver(this.options);
    }

    /**
     * 收集默认模式下可参与 imported-model 拾取的对象。
     * @returns 调用方可以修改的独立数组；内部高频路径应使用 getRaycastTargets()。
     */
    public collectRaycastTargets(): THREE.Object3D[] {
        return this.getRaycastTargets().slice();
    }

    /** @returns 当前共享只读快照；无 Viewer 的独立解析器保留直接遍历路径。 */
    public getRaycastTargets(): readonly THREE.Object3D[] {
        const sharedCandidates = this.viewer?.getImportedModelRaycastCandidates?.();
        if (sharedCandidates) return sharedCandidates;
        const candidates: THREE.Object3D[] = [];
        this.traverseImportedRaycastTree(this.options.scene, (object) => {
            if (!this.candidatePolicy.accepts(object)) {
                return;
            }
            candidates.push(object);
        });
        return candidates;
    }

    /**
     * 判断对象是否属于导入模型层级。
     * @param object 目标对象。
     * @returns 是否属于导入模型。
     */
    public isImportedModelObject(object: THREE.Object3D | null): boolean {
        if (this.viewer?.isImportedModelInstancedMesh(object)) {
            return true;
        }
        return this.selectionObjectResolver.isImportedModelObject(object);
    }

    /**
     * 将导入模型实例命中还原为现有 selection/topology 链可消费的 source Mesh 命中。
     *
     * @param hit Three.js 原始命中。
     * @returns source Mesh 命中；普通对象原样返回；旧 GPU 下标失效时返回 `null`。
     */
    public resolveInstancedHit(
        hit: THREE.Intersection<THREE.Object3D>,
    ): THREE.Intersection<THREE.Object3D> | null {
        if (!this.viewer) {
            return hit;
        }
        return this.viewer.resolveImportedModelInstancedHit(hit);
    }

    /**
     * 判断对象是否可作为 imported-model 的最终选中对象。
     * @param object 目标对象。
     * @returns 是否为可接受的 imported-model 选中对象。
     */
    public isImportedModelSelectionObject(object: THREE.Object3D | null): boolean {
        if (!object) {
            return false;
        }
        return this.isImportedModelObject(object);
    }

    /**
     * 将命中的 imported-model 子对象归一化为最终语义对象。
     * @param hitObject 射线命中对象。
     * @returns 归一化后的对象；未命中 imported-model 时返回 null。
     */
    public resolveSelectionObject(hitObject: THREE.Object3D): THREE.Object3D | null {
        if (isImportedTopologySelectionObject(hitObject)) {
            return hitObject;
        }
        return this.selectionObjectResolver.resolveSelectionObject(hitObject);
    }

    /**
     * 解析装配体移动应使用的变换根对象。
     *
     * @param hitObject 原始命中对象。
     * @returns 变换根对象；未命中 imported-model 时返回 `null`。
     */
    public resolveTransformRootObject(hitObject: THREE.Object3D): THREE.Object3D | null {
        const topologySourceMesh = resolveImportedTopologyVisibleSourceMesh(hitObject);
        if (topologySourceMesh) {
            const occurrenceRoot = this.viewer
                ?.resolveImportedModelInstancedOccurrenceRoot(topologySourceMesh) ?? null;
            if (occurrenceRoot) {
                return occurrenceRoot;
            }
            const sourceTransformRoot = this.selectionObjectResolver
                .resolveTransformRootObject(topologySourceMesh);
            if (sourceTransformRoot) {
                return sourceTransformRoot;
            }
        }
        if (this.viewer && hitObject instanceof THREE.Mesh) {
            const occurrenceRoot = this.viewer.resolveImportedModelInstancedOccurrenceRoot(hitObject);
            if (occurrenceRoot) {
                return occurrenceRoot;
            }
        }
        return this.selectionObjectResolver.resolveTransformRootObject(hitObject);
    }

    /**
     * 遍历 imported-model 射线候选树。
     *
     * @param root 遍历根节点。
     * @param visitor 候选访问回调。
     */
    private traverseImportedRaycastTree(root: THREE.Object3D, visitor: (object: THREE.Object3D) => void): void {
        const pendingObjects: THREE.Object3D[] = [root];
        while (pendingObjects.length > 0) {
            const object = pendingObjects.pop();
            if (!object || ImportedModelRaycastCandidatePolicy.shouldSkipSubtree(object)) {
                continue;
            }

            visitor(object);
            for (let index = object.children.length - 1; index >= 0; index -= 1) {
                const child = object.children[index];
                if (child) {
                    pendingObjects.push(child);
                }
            }
        }
    }


}
